import React, { useState, useEffect, useCallback } from 'react';
import MaxCharacter, { Sparkles } from './components/MaxCharacter';
import ThoughtBubble from './components/ThoughtBubble';
import MicrophoneDemo from './components/MicrophoneDemo';

import ToyImage from './components/ToyImage';
import CelebrationScreen from './components/CelebrationScreen';
import words from './data/words';
import './styles/animations.css';

/*
  Phase timeline per round
  ─────────────────────────────────────────────────────
  'intro'   → bubble floats in, word spoken        0ms
  'mic'     → microphone appears                2000ms
  'said'    → mic exits, bubble pops            5000ms
  'happy'   → toy animates, Max turns happy     5800ms
  ─────────────────────────────────────────────────────
  Next round auto-starts at                     7500ms
*/

function speakWord(word) {
  if (!window.speechSynthesis) return;
  window.speechSynthesis.cancel();

  // First utterance — slow and low-pitched (sad)
  const first = new SpeechSynthesisUtterance(word);
  first.rate  = 0.65;
  first.pitch = 0.6;
  first.volume = 1;

  // Second utterance — same sad tone, short pause after first ends
  const second = new SpeechSynthesisUtterance(word);
  second.rate  = 0.6;
  second.pitch = 0.55;
  second.volume = 1;

  first.onend = () => {
    setTimeout(() => window.speechSynthesis.speak(second), 400);
  };

  window.speechSynthesis.speak(first);
}

export default function App() {
  const [currentRound, setCurrentRound] = useState(0);
  const [phase, setPhase]               = useState('intro'); // intro | mic | said | happy
  const [gameComplete, setGameComplete] = useState(false);
  const [showToyInHands, setShowToyInHands] = useState(false);
  const [danceTick, setDanceTick]       = useState(0);

  const round = words[currentRound];

  /* ── Start / restart a round ── */
  const startRound = useCallback((roundIndex) => {
    setCurrentRound(roundIndex);
    setPhase('intro');
    setShowToyInHands(false);
  }, []);

  /* ── Phase sequencer ── */
  useEffect(() => {
    if (gameComplete) return;

    const timers = [];

    // Speak the word at 1000ms
    timers.push(setTimeout(() => speakWord(round.word), 1000));

    // Microphone appears at 2000ms
    timers.push(setTimeout(() => setPhase('mic'), 2000));

    // Word "said" at 5000ms — mic exits, bubble pops
    timers.push(setTimeout(() => setPhase('said'), 5000));

    // Toy swoops & Max happy at 5800ms
    timers.push(setTimeout(() => {
      setPhase('happy');
      setShowToyInHands(true);
    }, 5800));

    // Advance to next round at 7500ms
    timers.push(setTimeout(() => {
      if (currentRound < words.length - 1) {
        startRound(currentRound + 1);
      } else {
        setGameComplete(true);
      }
    }, 7500));

    return () => timers.forEach(clearTimeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRound, gameComplete]);

  /* ── Dance tick for celebration ── */
  useEffect(() => {
    if (!gameComplete) return;
    const id = setInterval(() => setDanceTick(t => t + 1), 400);
    return () => clearInterval(id);
  }, [gameComplete]);

  const handlePlayAgain = () => {
    setGameComplete(false);
    setDanceTick(0);
    startRound(0);
  };

  if (gameComplete) {
    return (
      <div style={rootStyle}>
        <CelebrationScreen onPlayAgain={handlePlayAgain}/>
      </div>
    );
  }

  const showBubble   = phase === 'intro' || phase === 'mic';
  const bubblePhase  = phase === 'said' ? 'popping' : 'normal';
  const showMic      = phase === 'mic';
  const maxState     = phase === 'happy' ? 'happy' : 'crying';
  const showSparkles = phase === 'happy';

  return (
    <div style={rootStyle}>
      {/* Sky background blobs */}
      <div style={cloudBlob(12, 8,  160, 70)}/>
      <div style={cloudBlob(68, 5,  130, 55)}/>
      <div style={cloudBlob(82, 68, 100, 45)}/>

      {/* Round indicator */}
      <div style={roundPillStyle}>
        {words.map((_, i) => (
          <div key={i} style={dotIndicator(i <= currentRound)}/>
        ))}
      </div>

      {/* ── Main stage ── */}
      <div style={stageStyle}>

        {/* Left: Max + thought bubble column */}
        <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:8, position:'relative'}}>
          {(showBubble || phase === 'said') && (
            <ThoughtBubble toyId={round.id} phase={bubblePhase}/>
          )}

          <div style={{position:'relative'}}>
            <MaxCharacter state={maxState} danceTick={danceTick}/>
            {showSparkles && <Sparkles/>}

            {/* Toy in hands (after happy) */}
            {showToyInHands && (
              <div className="anim-toy-appear" style={{
                position: 'absolute',
                bottom: 58,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 70,
                height: 70,
                zIndex: 2,
              }}>
                <ToyImage toyId={round.id}/>
              </div>
            )}
          </div>
        </div>

        {/* Right: Mic + label column */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 24,
          minWidth: 200,
        }}>
          <MicrophoneDemo visible={showMic}/>
        </div>
      </div>
    </div>
  );
}

/* ── Styles ── */
const rootStyle = {
  width: '100vw',
  height: '100vh',
  background: '#87CEEB',
  overflow: 'hidden',
  position: 'relative',
  fontFamily: "'Fredoka One', cursive",
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const stageStyle = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'clamp(24px, 6vw, 80px)',
  padding: '20px 40px',
  width: '100%',
  maxWidth: 900,
  flexWrap: 'wrap',
};

const roundPillStyle = {
  position: 'absolute',
  top: 20,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: 12,
  background: 'rgba(255,255,255,0.7)',
  border: '3px solid #111',
  borderRadius: 50,
  padding: '6px 18px',
  boxShadow: '3px 3px 0 #111',
};

const dotIndicator = (filled) => ({
  width: 18,
  height: 18,
  borderRadius: '50%',
  background: filled ? '#FFD600' : 'white',
  border: '3px solid #111',
  transition: 'background 0.3s',
});

function cloudBlob(leftPct, topPct, w, h) {
  return {
    position: 'absolute',
    left:   `${leftPct}%`,
    top:    `${topPct}%`,
    width:   w,
    height:  h,
    background: 'rgba(255,255,255,0.55)',
    borderRadius: '50%',
    filter: 'blur(8px)',
    pointerEvents: 'none',
    zIndex: 0,
  };
}
